import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Globe, User, Mail, Lock, Type, Check, Eye, Clock, Gift, CalendarDays, Sparkles, AlertCircle, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { SITE_TEMPLATES, getTemplatesList } from '@/config/templates';
import TemplatePreview from './TemplatePreview';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl ${className}`} {...props}>{children}</div>
);

const TRIAL_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 0, label: 'No expiry (free forever)' },
];

// Iter 175h — Build a clean subdomain slug from a brand name.
// Mirrors backend `_sanitize_subdomain`: lowercase, [a-z0-9-] only, no
// leading/trailing/duplicate hyphens, max 40 chars.
function slugify(raw) {
  if (!raw) return '';
  return String(raw)
    .toLowerCase()
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '') // strip accents
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
}

export default function MasterSiteNew() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('ecommerce');
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [trialEnabled, setTrialEnabled] = useState(true);
  const [trialDays, setTrialDays] = useState(14);
  const [skipBilling, setSkipBilling] = useState(true);
  const [form, setForm] = useState({
    name: '', domain: '', brand_name: '', admin_email: '', admin_password: '', admin_name: ''
  });

  // Iter 175h — domain mode: free EN subdomain OR client's own custom domain.
  // Default to free subdomain so masters can spin up a live preview link
  // even when the client hasn't bought a domain yet.
  const [domainMode, setDomainMode] = useState('subdomain'); // 'subdomain' | 'custom'
  const [subdomain, setSubdomain] = useState('');
  const [subdomainTouched, setSubdomainTouched] = useState(false);
  const [subdomainCheck, setSubdomainCheck] = useState({ status: 'idle', message: '' });

  // Auto-suggest a subdomain from brand name until the user types one manually.
  useEffect(() => {
    if (subdomainTouched) return;
    const slug = slugify(form.brand_name || form.name || '');
    setSubdomain(slug);
  }, [form.brand_name, form.name, subdomainTouched]);

  // Live availability check (debounced) whenever subdomain changes & mode is subdomain.
  useEffect(() => {
    if (domainMode !== 'subdomain') { setSubdomainCheck({ status: 'idle', message: '' }); return; }
    if (!subdomain || subdomain.length < 3) {
      setSubdomainCheck({ status: 'invalid', message: 'At least 3 characters' });
      return;
    }
    setSubdomainCheck({ status: 'checking', message: 'Checking availability…' });
    const t = setTimeout(async () => {
      try {
        const token = localStorage.getItem('masterToken');
        const r = await axios.get(`${API}/master/sites/check-subdomain`, {
          params: { slug: subdomain },
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = r.data || {};
        if (d.sanitized && d.sanitized !== subdomain) {
          // Auto-correct UI to the sanitized version
          setSubdomain(d.sanitized);
          return;
        }
        if (!d.valid) setSubdomainCheck({ status: 'invalid', message: 'Use lowercase letters, numbers and hyphens (3–40 chars)' });
        else if (d.reserved) setSubdomainCheck({ status: 'taken', message: 'This slug is reserved' });
        else if (!d.available) setSubdomainCheck({ status: 'taken', message: 'Already taken' });
        else setSubdomainCheck({ status: 'ok', message: 'Available!' });
      } catch (e) {
        setSubdomainCheck({ status: 'idle', message: '' });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [subdomain, domainMode]);

  const templates = getTemplatesList();

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const openPreview = (templateId) => {
    setPreviewTemplate(templateId);
    setShowPreview(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.brand_name || !form.admin_email || !form.admin_password) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (domainMode === 'custom' && !form.domain) {
      toast.error('Please enter the client\u2019s domain (or switch to a free EntrepreNatives subdomain)');
      return;
    }
    if (domainMode === 'subdomain') {
      if (!subdomain || subdomainCheck.status !== 'ok') {
        toast.error('Pick an available subdomain before creating the site');
        return;
      }
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('masterToken');
      const payload = {
        ...form,
        template: selectedTemplate,
        trial_days: trialEnabled ? trialDays : 0,
        skip_billing: skipBilling,
        free_forever: trialEnabled && trialDays === 0,
        use_subdomain: domainMode === 'subdomain',
        subdomain: domainMode === 'subdomain' ? subdomain : undefined,
        domain: domainMode === 'custom' ? form.domain : undefined,
      };
      await axios.post(`${API}/master/sites`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const trialMsg = trialEnabled
        ? trialDays === 0
          ? 'Site created with free forever access!'
          : `Site created with a ${trialDays}-day free trial!`
        : 'Site created successfully!';
      toast.success(trialMsg);
      if (domainMode === 'subdomain') {
        toast.message(`Live preview: ${subdomain}.entreprenatives.com`, { duration: 6000 });
      }
      navigate('/master/sites');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create site');
    } finally {
      setLoading(false);
    }
  };

  const selectedTemplateData = SITE_TEMPLATES[selectedTemplate] || Object.values(SITE_TEMPLATES)[0];

  return (
    <div className="max-w-4xl" data-testid="master-site-new">
      <button onClick={() => navigate('/master/sites')} className="flex items-center gap-2 text-[#9CA3AF] hover:text-white mb-6 transition-colors">
        <ArrowLeft size={18} /> Back to Sites
      </button>

      <GlassCard className="p-8">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center shadow-[0_0_20px_rgba(139,92,246,0.4)]">
            <Globe size={28} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Create New Site</h1>
            <p className="text-[#9CA3AF] text-sm">Set up a new client website with trial access</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="create-site-form">
          {/* Template Selection */}
          <div className="space-y-4">
            <h3 className="text-xs uppercase tracking-wider text-[#64748B] font-semibold">1. Choose a Template</h3>
            <p className="text-xs text-[#9CA3AF]">Select a style that matches your client's brand.</p>
            <div className="grid md:grid-cols-3 gap-4">
              {templates.map((template) => (
                <div key={template.id} className={`relative rounded-xl border transition-all duration-300 ${selectedTemplate === template.id ? 'border-[#8B5CF6]/50 bg-[#8B5CF6]/10 shadow-[0_0_20px_rgba(139,92,246,0.2)]' : 'border-white/5 hover:border-white/20 bg-white/[0.02]'}`}>
                  <button type="button" onClick={() => setSelectedTemplate(template.id)} className="w-full p-6 text-left" data-testid={`template-${template.id}`}>
                    {selectedTemplate === template.id && (
                      <div className="absolute top-3 right-12 w-5 h-5 bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] rounded-full flex items-center justify-center">
                        <Check size={12} className="text-white" />
                      </div>
                    )}
                    <div className="text-3xl mb-3">{template.preview}</div>
                    <h4 className="font-medium text-white mb-1">{template.name}</h4>
                    <p className="text-xs text-[#9CA3AF]">{template.description}</p>
                    <div className="flex gap-1 mt-4">
                      {['primary', 'secondary', 'background', 'text'].map((c) => (
                        <span key={c} className="w-4 h-4 rounded-full border border-white/10" style={{ backgroundColor: template.colors?.[c] || '#666' }} title={c} />
                      ))}
                    </div>
                  </button>
                  <button type="button" onClick={() => openPreview(template.id)} className="absolute top-3 right-3 w-8 h-8 bg-white/5 hover:bg-white/10 rounded-full flex items-center justify-center transition-colors" data-testid={`preview-${template.id}`}>
                    <Eye size={14} className="text-[#9CA3AF]" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Preview */}
          <div className="bg-white/[0.03] border border-white/5 p-4 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-24 h-16 flex items-center justify-center text-white text-xs rounded-lg" style={{ backgroundColor: selectedTemplateData?.colors?.primary || '#8B5CF6' }}>Hero</div>
              <div className="text-sm">
                <p className="text-white font-medium" style={{ fontFamily: selectedTemplateData?.fonts?.heading || 'Manrope' }}>{form.brand_name || 'Your Brand Name'}</p>
                <p className="text-[#9CA3AF] text-xs" style={{ fontFamily: selectedTemplateData?.fonts?.body || 'Manrope' }}>{selectedTemplateData?.defaultContent?.hero?.tagline || 'Your tagline here'}</p>
              </div>
            </div>
            <button type="button" onClick={() => openPreview(selectedTemplate)} className="flex items-center gap-2 text-[#A78BFA] hover:text-white transition-colors text-sm" data-testid="preview-selected-btn">
              <Eye size={16} /> Preview
            </button>
          </div>

          {/* Site Info */}
          <div className="space-y-4 pt-6 border-t border-white/5">
            <h3 className="text-xs uppercase tracking-wider text-[#64748B] font-semibold">2. Site Information</h3>

            <div>
              <Label htmlFor="name" className="text-[#94A3B8] text-sm flex items-center gap-2"><Type size={14} /> Site Name *</Label>
              <Input id="name" name="name" value={form.name} onChange={handleInputChange} placeholder="e.g., Sarah's Jewelry Store" required className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="site-name-input" />
              <p className="text-[10px] text-[#64748B] mt-1">Internal name for your reference</p>
            </div>

            <div>
              <Label htmlFor="brand_name" className="text-[#94A3B8] text-sm flex items-center gap-2"><Type size={14} /> Brand Name *</Label>
              <Input id="brand_name" name="brand_name" value={form.brand_name} onChange={handleInputChange} placeholder="e.g., Sarah's Fine Jewelry" required className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="brand-name-input" />
              <p className="text-[10px] text-[#64748B] mt-1">Displayed on storefront. We also use this to suggest a free subdomain.</p>
            </div>

            {/* Iter 175h — Domain mode toggle (free EN subdomain vs custom domain) */}
            <div className="pt-2">
              <Label className="text-[#94A3B8] text-sm flex items-center gap-2 mb-3"><Globe size={14} /> Web Address *</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="domain-mode-toggle">
                <button
                  type="button"
                  onClick={() => setDomainMode('subdomain')}
                  className={`p-4 rounded-xl border text-left transition-all ${domainMode === 'subdomain' ? 'bg-[#8B5CF6]/15 border-[#8B5CF6]/40 shadow-[0_0_18px_rgba(139,92,246,0.18)]' : 'bg-white/[0.02] border-white/5 hover:border-white/15'}`}
                  data-testid="domain-mode-subdomain"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <Sparkles size={14} className={domainMode === 'subdomain' ? 'text-[#A78BFA]' : 'text-white/40'} />
                    <span className={`text-sm font-bold ${domainMode === 'subdomain' ? 'text-white' : 'text-white/70'}`}>Free EntrepreNatives subdomain</span>
                  </div>
                  <p className="text-[11px] text-white/55">Ship a live preview link today. Client can add their own domain later.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setDomainMode('custom')}
                  className={`p-4 rounded-xl border text-left transition-all ${domainMode === 'custom' ? 'bg-[#3B82F6]/15 border-[#3B82F6]/40 shadow-[0_0_18px_rgba(59,130,246,0.18)]' : 'bg-white/[0.02] border-white/5 hover:border-white/15'}`}
                  data-testid="domain-mode-custom"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <Globe size={14} className={domainMode === 'custom' ? 'text-[#60A5FA]' : 'text-white/40'} />
                    <span className={`text-sm font-bold ${domainMode === 'custom' ? 'text-white' : 'text-white/70'}`}>Use a custom domain</span>
                  </div>
                  <p className="text-[11px] text-white/55">Client already owns a domain like <code className="text-white/70">sarahjewelry.com</code>.</p>
                </button>
              </div>

              {domainMode === 'subdomain' ? (
                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 focus-within:border-[#A78BFA]/50 transition-colors">
                    <Input
                      value={subdomain}
                      onChange={(e) => { setSubdomain(slugify(e.target.value)); setSubdomainTouched(true); }}
                      placeholder="sarahs-jewelry"
                      className="bg-transparent border-0 text-white placeholder:text-[#64748B] h-7 p-0 focus-visible:ring-0 focus-visible:ring-offset-0 flex-1"
                      data-testid="subdomain-input"
                    />
                    <span className="text-white/60 text-sm font-mono shrink-0">.entreprenatives.com</span>
                  </div>
                  <SubdomainStatus check={subdomainCheck} />
                  {subdomainCheck.status === 'ok' && (
                    <div className="p-3 rounded-xl bg-[#8B5CF6]/5 border border-[#8B5CF6]/15 text-xs text-[#C4B5FD] flex items-start gap-2" data-testid="subdomain-preview-url">
                      <Eye size={13} className="mt-0.5 shrink-0" />
                      <span>
                        Live preview will be available at{' '}
                        <strong className="text-white font-mono">
                          {subdomain}.entreprenatives.com
                        </strong>
                        . Share this URL with the client to show progress.
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-4">
                  <Input
                    id="domain"
                    name="domain"
                    value={form.domain}
                    onChange={handleInputChange}
                    placeholder="e.g., sarahjewelry.com"
                    className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl"
                    data-testid="site-domain-input"
                  />
                  <p className="text-[10px] text-[#64748B] mt-1">Client can add more domains later from their dashboard.</p>
                </div>
              )}
            </div>
          </div>

          {/* Admin Account */}
          <div className="space-y-4 pt-6 border-t border-white/5">
            <h3 className="text-xs uppercase tracking-wider text-[#64748B] font-semibold">3. Client Login Credentials</h3>
            <p className="text-xs text-[#9CA3AF]">Your client will use these to access their dashboard</p>
            <div>
              <Label htmlFor="admin_name" className="text-[#94A3B8] text-sm flex items-center gap-2"><User size={14} /> Client Name</Label>
              <Input id="admin_name" name="admin_name" value={form.admin_name} onChange={handleInputChange} placeholder="e.g., Sarah Johnson" className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="admin-name-input" />
            </div>
            <div>
              <Label htmlFor="admin_email" className="text-[#94A3B8] text-sm flex items-center gap-2"><Mail size={14} /> Client Email *</Label>
              <Input id="admin_email" name="admin_email" type="email" value={form.admin_email} onChange={handleInputChange} placeholder="e.g., sarah@jewelry.com" required className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="admin-email-input" />
            </div>
            <div>
              <Label htmlFor="admin_password" className="text-[#94A3B8] text-sm flex items-center gap-2"><Lock size={14} /> Client Password *</Label>
              <Input id="admin_password" name="admin_password" type="password" value={form.admin_password} onChange={handleInputChange} placeholder="Create a secure password" required className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="admin-password-input" />
            </div>
          </div>

          {/* Trial & Billing */}
          <div className="space-y-5 pt-6 border-t border-white/5">
            <h3 className="text-xs uppercase tracking-wider text-[#64748B] font-semibold">4. Trial & Billing Access</h3>
            <p className="text-xs text-[#9CA3AF]">Let your client test everything before requiring payment</p>

            {/* Trial Toggle */}
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#8B5CF6]/10 flex items-center justify-center">
                    <Gift size={16} className="text-[#A78BFA]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">Free Trial Period</p>
                    <p className="text-[10px] text-slate-500">Client can use all features without billing</p>
                  </div>
                </div>
                <button type="button" onClick={() => setTrialEnabled(!trialEnabled)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${trialEnabled ? 'bg-[#8B5CF6]' : 'bg-white/10'}`}
                  data-testid="trial-toggle">
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${trialEnabled ? 'left-[22px]' : 'left-0.5'}`} />
                </button>
              </div>

              {trialEnabled && (
                <div className="space-y-3 pt-2">
                  <Label className="text-[#94A3B8] text-xs flex items-center gap-1.5"><CalendarDays size={12} /> Trial Duration</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {TRIAL_OPTIONS.map((opt) => (
                      <button key={opt.value} type="button" onClick={() => setTrialDays(opt.value)}
                        className={`py-2.5 px-3 rounded-xl text-xs font-medium border transition-all ${trialDays === opt.value
                          ? 'bg-[#8B5CF6]/15 border-[#8B5CF6]/30 text-[#A78BFA]'
                          : 'bg-white/[0.02] border-white/5 text-slate-500 hover:text-white hover:border-white/10'
                        }`}
                        data-testid={`trial-${opt.value}`}>
                        {opt.value === 0 ? <span className="flex items-center gap-1 justify-center"><Gift size={11} /> Free forever</span> : opt.label}
                      </button>
                    ))}
                  </div>

                  {trialDays > 0 && (
                    <div className="p-3 rounded-xl bg-[#8B5CF6]/5 border border-[#8B5CF6]/15 text-xs text-[#C4B5FD] flex items-start gap-2">
                      <Clock size={13} className="mt-0.5 shrink-0" />
                      <span>Client gets <strong>{trialDays} days</strong> of full access. When the trial expires, they'll be prompted to add payment info to keep their site active.</span>
                    </div>
                  )}
                  {trialDays === 0 && (
                    <div className="p-3 rounded-xl bg-[#00CC99]/5 border border-[#00CC99]/15 text-xs text-[#6EE7B7] flex items-start gap-2">
                      <Gift size={13} className="mt-0.5 shrink-0" />
                      <span>This site will be <strong>free forever</strong> — no billing required. You can change this later from the site's billing settings.</span>
                    </div>
                  )}
                </div>
              )}

              {!trialEnabled && (
                <div className="p-3 rounded-xl bg-[#F59E0B]/5 border border-[#F59E0B]/15 text-xs text-[#FCD34D] flex items-start gap-2">
                  <Clock size={13} className="mt-0.5 shrink-0" />
                  <span>No trial period. Client will need to set up billing immediately to activate their site.</span>
                </div>
              )}
            </div>

            {/* Skip Billing Toggle */}
            {trialEnabled && (
              <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center">
                    <Lock size={16} className="text-[#60A5FA]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">Skip Billing Setup</p>
                    <p className="text-[10px] text-slate-500">Client can explore without entering payment info</p>
                  </div>
                </div>
                <button type="button" onClick={() => setSkipBilling(!skipBilling)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${skipBilling ? 'bg-[#3B82F6]' : 'bg-white/10'}`}
                  data-testid="skip-billing-toggle">
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${skipBilling ? 'left-[22px]' : 'left-0.5'}`} />
                </button>
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex gap-4 pt-6">
            <button type="button" onClick={() => navigate('/master/sites')} className="flex-1 py-3 rounded-xl border border-white/10 text-[#94A3B8] hover:bg-white/5 transition-colors">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 py-3 rounded-xl font-medium text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-50 transition-all" data-testid="create-site-submit">
              {loading ? 'Creating...' : 'Create Site'}
            </button>
          </div>
        </form>
      </GlassCard>

      {/* What happens next */}
      <GlassCard className="mt-6 p-6">
        <h3 className="font-semibold text-white mb-4">What happens next?</h3>
        <ol className="space-y-3 text-sm text-[#9CA3AF]">
          {[
            `Site is created with the ${selectedTemplateData?.name || 'selected'} template`,
            domainMode === 'subdomain'
              ? <>Share live preview URL: <code className="text-[#A78BFA] font-mono">{subdomain || 'yoursite'}.entreprenatives.com</code></>
              : 'Client adds DNS records to point their domain at EntrepreNatives',
            trialEnabled && trialDays > 0
              ? `Client gets a ${trialDays}-day free trial — no billing needed`
              : trialEnabled && trialDays === 0
                ? 'Client gets free forever access — no billing needed'
                : 'Client will need to set up billing to activate',
            'Share login credentials with your client',
            trialEnabled && trialDays > 0
              ? 'When trial expires, client adds payment info to continue'
              : (domainMode === 'subdomain'
                ? 'Client can upgrade to a custom domain anytime from Settings'
                : 'Client adds custom domain in Settings'),
          ].map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className="w-6 h-6 bg-[#8B5CF6]/20 text-[#A78BFA] rounded-full flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </GlassCard>

      {showPreview && <TemplatePreview templateId={previewTemplate} brandName={form.brand_name} onClose={() => setShowPreview(false)} />}
    </div>
  );
}

// Iter 175h — Inline pill that shows subdomain availability status.
function SubdomainStatus({ check }) {
  if (!check.status || check.status === 'idle') return null;
  const map = {
    checking: { tone: 'text-white/60 bg-white/5 border-white/10', icon: <Loader2 size={11} className="animate-spin" /> },
    ok:       { tone: 'text-[#86EFAC] bg-emerald-500/10 border-emerald-500/25', icon: <Check size={11} /> },
    invalid:  { tone: 'text-[#FCD34D] bg-amber-500/10 border-amber-500/25', icon: <AlertCircle size={11} /> },
    taken:    { tone: 'text-[#FCA5A5] bg-red-500/10 border-red-500/25', icon: <AlertCircle size={11} /> },
  };
  const meta = map[check.status] || map.invalid;
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border ${meta.tone}`} data-testid={`subdomain-status-${check.status}`}>
      {meta.icon} {check.message}
    </div>
  );
}
